/**
 * Task Z: GPS-geofenced clock in / out and the device class rule for the cashier (checkout needs a tablet).
 * Pure functions shared by the API (enforcement) and the apps (messages, pre-checks).
 */

/** (Task DD2) legacy column — staff self-clock ALWAYS needs GPS now; the value is kept but ignored */
export type ClockGpsMode = "auto" | "on" | "off";
export type MockPolicy = "flag" | "block";
export interface BranchGeo {
  lat: number | null; lng: number | null;
  radiusM: number; accuracyM: number; clockGps: ClockGpsMode; mockPolicy: MockPolicy;
}
export interface GeoFix { lat: number; lng: number; accuracy: number; timestamp: number; mock?: boolean | null }

export const GEO_RADIUS_MIN = 30;
export const GEO_RADIUS_MAX = 1000;
export const GEO_RADIUS_DEFAULT = 100;
export const GEO_ACCURACY_DEFAULT = 50;
export const GEO_ACCURACY_MAX = 500;
/** a fix older than this (or this far in the future: device clock skew) is refused */
export const GEO_MAX_AGE_MS = 2 * 60_000;
/** a fix this vague can't place anyone in a shop */
export const GEO_UNUSABLE_ACCURACY_M = 1000;

const R = 6_371_008.8; // mean Earth radius (m)
const rad = (d: number) => (d * Math.PI) / 180;
/** Great-circle distance in metres (haversine). */
export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = rad(lat2 - lat1), dLng = rad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

export const hasLocation = (b: Pick<BranchGeo, "lat" | "lng">) => b.lat != null && b.lng != null && Number.isFinite(b.lat) && Number.isFinite(b.lng);
/**
 * (Task DD2, owner decision) staff clock in / out ONLY on their own logged-in phone, and GPS is ALWAYS required — there
 * is no "off" / "auto" bypass any more (the old clock_gps setting is ignored). Without a shop location nobody can clock.
 */
export function gpsRequired(_b?: Pick<BranchGeo, "lat" | "lng">): boolean {
  return true;
}
/** The owner should be prompted (and staff can't clock): the shop location isn't set yet. */
export const gpsNeedsLocation = (b: Pick<BranchGeo, "lat" | "lng">) => !hasLocation(b);

export type GeoVerdict =
  | { ok: true; check: "inside" | "not_required" | null; distanceM: number | null; mock: boolean }
  | { ok: false; code: "no_clock_location" | "location_required" | "location_stale" | "location_inaccurate" | "outside_geofence" | "mock_location"; distanceM?: number; radiusM?: number; accuracyM?: number };

/**
 * Decide an own-device clock in / out. `fix` is what the phone sent (null = nothing). Allowed when
 * distance - min(accuracy, tolerance) <= radius, the fix is at most 2 minutes old and usable (accuracy <= 1 km).
 * (Task DD2) a branch without a shop location refuses every own-device clock in / out (no_clock_location).
 */
export function checkGeofence(b: BranchGeo, fix: GeoFix | null, now = Date.now()): GeoVerdict {
  const mock = !!fix?.mock;
  if (!hasLocation(b)) return { ok: false, code: "no_clock_location" };
  if (!fix) return { ok: false, code: "location_required" };
  if (!Number.isFinite(fix.timestamp) || Math.abs(now - fix.timestamp) > GEO_MAX_AGE_MS) return { ok: false, code: "location_stale" };
  if (!(fix.accuracy >= 0) || fix.accuracy > GEO_UNUSABLE_ACCURACY_M) return { ok: false, code: "location_inaccurate", accuracyM: Math.round(fix.accuracy) };
  if (mock && b.mockPolicy === "block") return { ok: false, code: "mock_location" };
  const distanceM = Math.round(haversineM(b.lat!, b.lng!, fix.lat, fix.lng));
  const margin = Math.min(fix.accuracy, b.accuracyM);
  if (distanceM - margin > b.radiusM) return { ok: false, code: "outside_geofence", distanceM, radiusM: b.radiusM, accuracyM: Math.round(fix.accuracy) };
  return { ok: true, check: "inside", distanceM, mock };
}

/** "35 m" / "1.2 km" */
export function formatDistance(m: number): string {
  return m < 1000 ? Math.round(m) + " m" : (Math.round(m / 100) / 10).toFixed(1) + " km";
}

// ------------------------------------------------------------------ device class (Task Z: the cashier needs a tablet)
export type DeviceClass = "phone" | "tablet" | "desktop";
export interface DeviceSignals {
  /** Capacitor platform: 'ios' | 'android' | 'web' */
  platform: string;
  /** Capacitor Device.getInfo().model, e.g. "iPad13,1", "iPhone15,2", "SM-X710" (optional) */
  model?: string | null;
  userAgent: string;
  /** screen size in CSS px (= dp in the Android WebView / points on iOS) */
  screenW: number; screenH: number;
  maxTouchPoints?: number;
}
/** Smallest-width rule: Android's own tablet threshold is sw600dp. */
export const TABLET_MIN_SIDE = 600;

/**
 * Phone, tablet or desktop — by device class, not orientation: an iPad (incl. iPadOS 13+ that reports itself as a
 * Mac in the user agent) or a device whose SMALLEST screen side is >= 600 dp is a tablet; a touch device below that is
 * a phone; a non-touch web browser is a desktop / laptop.
 */
export function deviceClass(s: DeviceSignals): DeviceClass {
  const ua = s.userAgent || "";
  const model = s.model || "";
  const touch = (s.maxTouchPoints ?? 0) > 1;
  const minSide = Math.min(s.screenW, s.screenH);
  if (/^iPad/i.test(model) || /iPad/.test(ua)) return "tablet";
  if (/^iPhone|^iPod/i.test(model) || /iPhone|iPod/.test(ua)) return "phone";
  // iPadOS 13+ Safari / WKWebView: "Macintosh" UA with a touch screen
  if (/Macintosh/.test(ua) && (touch || s.platform === "ios")) return "tablet";
  if (s.platform === "ios") return minSide >= TABLET_MIN_SIDE ? "tablet" : "phone";
  if (s.platform === "android" || /Android/.test(ua)) return minSide >= TABLET_MIN_SIDE ? "tablet" : "phone";
  // web browser elsewhere
  if (touch || /Mobi/.test(ua)) return minSide >= TABLET_MIN_SIDE ? "tablet" : "phone";
  return "desktop";
}

/** Checkout (收银) for a cashier account: tablets and desktop / laptop browsers only — never on a phone. */
export const checkoutAllowedOn = (role: string | null | undefined, cls: DeviceClass) => role !== "cashier" || cls !== "phone";
